import React from 'react';
import { Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TFunction } from 'i18next';
import { getClickableSentenceProps } from './StatementXmlPreview.utils';
import type { StatementNodeData, StatementSentenceRow } from './types';

type StatementXmlPreviewTableProps = {
  rows: StatementSentenceRow[];
  nodes: StatementNodeData[];
  dense: boolean;
  maxVisibleRows: number;
  showRowNumbers: boolean;
  sx?: SxProps<Theme>;
  showGlossaryCodeOnHover: boolean;
  onNodeClick?: (node: StatementNodeData) => void;
  t: TFunction;
};

export const StatementXmlPreviewTable: React.FC<StatementXmlPreviewTableProps> = ({
  rows,
  nodes,
  dense,
  maxVisibleRows,
  showRowNumbers,
  sx,
  showGlossaryCodeOnHover,
  onNodeClick,
  t,
}) => {
  const maxHeight = maxVisibleRows * (dense ? 38 : 48) + 48;

  return (
    <TableContainer
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        maxHeight,
        ...sx,
      }}
    >
      <Table stickyHeader size={dense ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            {showRowNumbers && <TableCell sx={{ width: 56 }}>#</TableCell>}
            <TableCell sx={{ width: { xs: 120, md: 180 } }}>{t('statementXml.tableClpTag')}</TableCell>
            <TableCell>{t('statementXml.tableSentence')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={showRowNumbers ? 3 : 2}>
                <Typography variant="body2" color="text.secondary">
                  {t('statementXml.tableEmpty')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row, index) => {
            const isNodeRow = row.sourceKind === 'node';
            const matchingNode = isNodeRow && row.id ? nodes.find((node) => node.id === row.id) : null;
            const clickableSentenceProps = getClickableSentenceProps(onNodeClick, matchingNode);

            const rowContent = (
              <TableRow
                hover
                sx={{
                  '& .MuiTableCell-root': {
                    py: dense ? 0.75 : 1,
                  },
                }}
              >
                {showRowNumbers && <TableCell>{index + 1}</TableCell>}
                <TableCell>
                  <Chip
                    size="small"
                    color={row.code ? 'warning' : 'default'}
                    label={row.code || t('statementXml.unassigned')}
                    variant={row.code ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  <Box
                    component="span"
                    {...clickableSentenceProps}
                    sx={{
                      cursor: onNodeClick && matchingNode ? 'pointer' : 'default',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {row.text}
                  </Box>
                </TableCell>
              </TableRow>
            );

            if (!showGlossaryCodeOnHover || !row.code) return <React.Fragment key={row.rowId}>{rowContent}</React.Fragment>;

            return (
              <Tooltip key={row.rowId} title={t('clp.tooltipCode', { code: row.code })} arrow placement="top">
                {rowContent}
              </Tooltip>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
